import React, {
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import cn from "classnames";
import s from "./AnchorMenu.module.scss";
import c from "../../styles/container.module.scss";
import List from "./components/List";
import Item from "./components/Item";
import CTAItem from "./components/CTAItem";
import MobileMenu from "./components/MobileMenu";
import ExpandButton from "./components/ExpandButton";
import useEventListener from "../../lib/utils/useEventListener";

const subComponents = {
  List,
  Item,
  CTAItem,
  MobileMenu,
  ExpandButton,
};
type SubComponents = typeof subComponents;

type Props = {
  children: ReactNode;
  stickyOffset?: number;
} & HTMLAttributes<HTMLDivElement>;

const AnchorMenu: CWS<Props, SubComponents> = ({ children, ...rest }) => {
  const [originalOffsetTop, setOriginalOffsetTop] = useState<number>(-1);
  const [sticky, setSticky] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const measureOffsetTop = () => {
      if (navRef.current) {
        const offsetTop = navRef.current.offsetTop;
        setOriginalOffsetTop(offsetTop);
        setIsReady(true);
      }
    };

    const timeoutId = setTimeout(measureOffsetTop, 100);

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(measureOffsetTop);
    });

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const resolveSticky = () => {
    if (!navRef.current || !isReady || originalOffsetTop <= -1) {
      return;
    }

    const navOffset = navRef.current.offsetTop - 2;
    const shouldBeSticky =
      window.pageYOffset > navOffset && window.pageYOffset > originalOffsetTop;
    setSticky(shouldBeSticky);
  };

  useEventListener("scroll", resolveSticky);

  useEventListener("resize", () => {
    if (navRef.current) {
      const newOffsetTop = navRef.current.offsetTop;
      setOriginalOffsetTop(newOffsetTop);
    }
  });

  return (
    <>
      <nav
        ref={navRef}
        className={cn(s.anchorMenu, {
          [s.sticky]: sticky && isReady,
        })}
        {...rest}
      >
        <div className={cn(s.wrapper, c.xWide)}>{children}</div>
      </nav>
      {sticky && isReady && <div className={s.spacer} />}
    </>
  );
};

AnchorMenu.List = List;
AnchorMenu.Item = Item;
AnchorMenu.CTAItem = CTAItem;
AnchorMenu.MobileMenu = MobileMenu;
AnchorMenu.ExpandButton = ExpandButton;

export default AnchorMenu;
